import React, { useEffect } from 'react';
import { Checkbox, Col, Flex, Form, Modal, Row } from 'antd';
import type { FormInstance } from 'antd';
import {
  CustomButton,
  CustomDynamicSelect,
  CustomInput,
  CustomParagraph,
  CustomTitle,
} from '../../components';
import PhoneInput from 'react-phone-input-2';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { prefferedLanguages } from '../../common/static_data';
import PreferredChannelSelector from '../Settings/DirectContactList/PreferredChannelSelector';

interface Contact {
  id: string;
  name: string;
  title?: string;
  email?: string;
  phone_number?: string;
  preferred_language?: string;
  is_primary_contact?: boolean;
  preferred_channel?: SavedPreferredChannel;
}

interface ContactFormData {
  name: string;
  title: string;
  email?: string;
  phone_number?: string;
  preferred_language?: string;
  preferred_channel?: SavedPreferredChannel;
  is_primary_contact?: boolean;
  id?: string | null;
}
export type PreferredChannel = 'phone' | 'email' | 'sms' | 'none';

const NO_PREFERENCE = 'no_preference';
type SavedPreferredChannel = PreferredChannel | typeof NO_PREFERENCE;

interface AddNewContactModalProps {
  title: string;
  setEditingContact: (contact: Contact | null) => void;
  contactList: Contact[];
  form: FormInstance<ContactFormData>;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  apiCall: (data: ContactFormData) => void;
  editContact?: Contact | null;
  preferredChannel: PreferredChannel | null;
  setPreferredChannel: (value: PreferredChannel) => void;
}

/**
 * Modal component for adding or editing contact information
 *
 * @param title - Modal title text
 * @param setEditingContact - Function to set the contact being edited
 * @param contactList - List of existing contacts for validation
 * @param form - Ant Design form instance
 * @param modalOpen - Whether the modal is open
 * @param setModalOpen - Function to control modal visibility
 * @param apiCall - Function called when form is submitted
 * @param editContact - Contact being edited (optional)
 */
const AddNewContactModal: React.FC<AddNewContactModalProps> = ({
  title,
  setEditingContact,
  contactList,
  form,
  modalOpen,
  setModalOpen,
  apiCall,
  editContact,
  preferredChannel,
  setPreferredChannel,
}) => {
  const onFinish = (values: ContactFormData) => {
    if (values && Object.keys(values).length > 0) {
      const model: ContactFormData = {
        ...values,
        // Selector uses 'none'; backend choice is 'no_preference'
        preferred_channel:
          preferredChannel === 'none'
            ? NO_PREFERENCE
            : (preferredChannel ?? 'phone'),
      };
      apiCall(model);
    }
  };
  useEffect(() => {
    if (modalOpen) {
      const savedPreferredChannel = editContact?.preferred_channel;
      setPreferredChannel(
        savedPreferredChannel === NO_PREFERENCE
          ? 'none'
          : (savedPreferredChannel ?? 'phone')
      );
      form.setFieldsValue({
        is_primary_contact: editContact?.id
          ? editContact?.is_primary_contact
          : contactList?.length === 0,
        id: editContact?.id || null,
      });
    }
  }, [
    modalOpen,
    contactList,
    form,
    editContact?.id,
    editContact?.is_primary_contact,
    setPreferredChannel,
    editContact?.preferred_channel,
  ]);

  useEffect(() => {
    const value = form.getFieldsValue()?.preferred_language;
    if (
      value &&
      (value === 'english' || value?.toLowerCase() === 'english(us)')
    ) {
      form.setFieldsValue({
        preferred_language: 'English',
      });
    }
  });

  const handleCancel = () => {
    form.resetFields();
    setModalOpen(false);
    setEditingContact(null);
  };

  return (
    <Modal
      title=''
      centered
      open={modalOpen}
      onCancel={() => setModalOpen(false)}
      width={542}
      className='contact-modal'
      // footer buttons
      footer={[
        <React.Fragment key='footer-actions'>
          <Flex gap='16px' justify='end'>
            <CustomButton
              key='button1'
              onClick={handleCancel}
              text={'Cancel'}
              bgColor='transparent'
              color='black'
              fontSize='14px'
              fontWeight='600'
              boxShadow='none'
              height='40px'
            />
            <CustomButton
              key='button2'
              text={'Save'}
              bgColor='black'
              color='white'
              height='40px'
              width='auto'
              fontSize='14px'
              fontWeight='600'
              boxShadow='none'
              borderRadius='8px'
              onClick={(e) => {
                e.stopPropagation();
                form.submit();
              }}
            />
          </Flex>
        </React.Fragment>,
      ]}
    >
      <CustomTitle
        style={{ position: 'relative', left: '-5px' }}
        text={title}
        fontSize='24px'
        lineHeight='32px'
        fontWeight='700'
        marginBottom='0px'
      />
      <main
        style={{
          marginBottom: '20px',
          marginTop: '20px',
          marginRight: '-3px',
          marginLeft: '-5px',
        }}
      >
        <Form
          form={form}
          name='AddNewContact'
          layout='vertical'
          onFinish={onFinish}
          autoComplete='off'
          initialValues={{ preferred_language: 'English' }}
        >
          <Row gutter={[0, 16]}>
            {/* name  */}
            <Col span={16}>
              <Form.Item
                className='custom-input form-input-style'
                label='Name'
                name='name'
                rules={[
                  { required: true, message: 'Please enter name' },
                  {
                    pattern: /^[A-Za-z\s'-]+$/,
                    message: 'Name must contain only letters',
                  },
                ]}
              >
                <CustomInput
                  placeholder='Enter name'
                  height='36px'
                  width='100%'
                  border='0px'
                  bgColor='var(--light-gray)'
                  borderRadius='8px'
                  placeholderColor='var(--text-secondary)'
                  lineHeight='38px'
                />
              </Form.Item>
            </Col>
            {/* title  */}
            <Col span={16}>
              <Form.Item
                className='custom-input form-input-style'
                label='Title'
                name='title'
                rules={[{ required: true, message: 'Please enter title' }]}
              >
                <CustomInput
                  placeholder='Enter title'
                  height='36px'
                  width='100%'
                  border='0px'
                  bgColor='var(--light-gray)'
                  borderRadius='8px'
                  placeholderColor='var(--text-secondary)'
                  lineHeight='38px'
                />
              </Form.Item>
            </Col>
            <Col span={16} style={{ display: 'none' }}>
              <Form.Item
                className='custom-input form-input-style'
                name='country_code'
                rules={[{ required: false, message: 'Please enter title' }]}
              >
                <CustomInput
                  placeholder='Enter title'
                  height='36px'
                  width='100%'
                  border='0px'
                  bgColor='var(--light-gray)'
                  borderRadius='8px'
                  placeholderColor='var(--text-secondary)'
                  lineHeight='38px'
                />
              </Form.Item>
            </Col>
            {/* email  */}
            <Col span={16}>
              <Form.Item
                className='custom-input form-input-style'
                label='Email'
                name='email'
                dependencies={['phone_number']}
                rules={[
                  { type: 'email', message: 'Please enter a valid email' },
                  {
                    validator: (_, value) => {
                      const phone = form.getFieldValue('phone_number');
                      if (!value && !phone) {
                        return Promise.reject(
                          'Please provide either Email or Phone'
                        );
                      }
                      if (!value) return Promise.resolve();

                      const currentId = form.getFieldValue('id');
                      const exists = contactList.some(
                        (c) =>
                          c.email?.toLowerCase() === value.toLowerCase() &&
                          c.id !== currentId
                      );
                      return exists
                        ? Promise.reject('This email is already in use')
                        : Promise.resolve();
                    },
                  },
                ]}
              >
                <CustomInput
                  placeholder='Enter email'
                  height='36px'
                  width='100%'
                  border='0px'
                  bgColor='var(--light-gray)'
                  borderRadius='8px'
                  placeholderColor='var(--text-secondary)'
                  lineHeight='38px'
                />
              </Form.Item>
            </Col>
            {/* phone number  */}
            <Col span={16}>
              <div className=''>
                <Form.Item
                  label={'Phone number'}
                  name='phone_number'
                  style={{ marginBottom: '0px' }}
                  dependencies={['email']}
                  rules={[
                    {
                      validator: (_, value) => {
                        const email = form.getFieldValue('email');
                        if (!value && !email) {
                          return Promise.reject(
                            'Please provide either Email or Phone'
                          );
                        }
                        if (value && !isValidPhoneNumber(value)) {
                          return Promise.reject('Invalid phone number');
                        }

                        if (!value) return Promise.resolve();

                        const currentId = form.getFieldValue('id');
                        const exists = contactList.some(
                          (c) => c.phone_number === value && c.id !== currentId
                        );

                        return exists
                          ? Promise.reject(
                              'This phone number is already in use'
                            )
                          : Promise.resolve();
                      },
                    },
                  ]}
                >
                  <PhoneInput
                    country={'us'}
                    value={form.getFieldValue('phone_number')}
                    onChange={(value, countryData, e, formattedValue) => {
                      form.setFieldsValue({ phone_number: formattedValue });
                    }}
                  />
                </Form.Item>
              </div>
            </Col>
            {/* Preffered language  */}

            <Col span={16}>
              <Form.Item
                className='custom-input form-input-style font-black-color'
                label='Preferred language'
                name='preferred_language'
                rules={[{ required: false, message: 'Please select value' }]}
                style={{ marginBottom: '0px' }}
              >
                <CustomDynamicSelect
                  placeholder='Select'
                  height='36px'
                  border='0px'
                  bgColor='var(--light-gray)'
                  textColor='var(--text-secondary)'
                  borderRadius='8px'
                  lineHeight='16px'
                  options={prefferedLanguages}
                />
              </Form.Item>
              <CustomParagraph
                text={'AI-generated communications will be in this language'}
                fontSize='12px'
                fontWeight='400'
                lineHeight='20px'
                marginBottom='0'
                marginTop={'8px'}
                color={'#5e5e5e'}
              />
            </Col>
            <PreferredChannelSelector
              value={preferredChannel ?? 'phone'}
              onChange={setPreferredChannel}
              isDirect={true}
            />
            {/* primary contact  */}
            <Col span={24}>
              <Form.Item
                label=''
                name='is_primary_contact'
                valuePropName='checked'
              >
                <Checkbox>Make primary contact</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </main>
    </Modal>
  );
};

export default AddNewContactModal;
